import { DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import React from 'react';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { VisualPicker } from '../../../CommonComponents/OPL/VisualPicker/VisualPicker';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import {
  PLANS_MAX_COUNT,
  PLANS_MIN_COUNT,
} from '../../../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/RetirementSideBySide.meta';
import { ComparePlansProps, useComparePlans } from './useComparePlans';
import { SortableComparePlanField } from '../SortableComparePlanField/SortableComparePlanField';

const HEADER_ID = 'pdf--edit-compare-plans-modal--header';
const FORM_ID = 'pdf--edit-compare-plans-modal--form';

export const ComparePlans = (props: ComparePlansProps) => {
  const {
    fields,
    setValue,
    plansById,
    selectedPlansCount,
    canReorder,
    onSubmit,
    handleDragEnd,
  } = useComparePlans(props);

  const sortablePlanIds = fields.map(field => field.id);

  const renderPlanField = (field: typeof fields[number], index: number) => {
    const plan = plansById.get(field.id);
    const label = plan?.name ?? '';

    if (canReorder) {
      return (
        <SortableComparePlanField
          key={field.key}
          planId={field.id}
          label={label}
          checked={field.selected}
          onChange={evt => setValue(`planFields.${index}.selected`, evt.target.checked)}
          disabled={!field.selected && selectedPlansCount === PLANS_MAX_COUNT}
        />
      );
    }

    return (
      <VisualPicker
        key={field.key}
        variant="checkbox"
        label={label}
        checked={field.selected}
        onChange={evt => setValue(`planFields.${index}.selected`, evt.target.checked)}
        disabled={!field.selected && selectedPlansCount === PLANS_MAX_COUNT}
        mt="8"
      />
    );
  };

  return (
    <Modal
      isOpen
      onRequestClose={props.onClose}
      aria={{ labelledby: HEADER_ID }}
      shouldCloseOnOverlayClick={false}
    >
      <ModalHeader as="h2" id={HEADER_ID}>
        Edit Compare Plans
      </ModalHeader>
      <ModalCloseButton onClick={props.onClose} />

      <ModalBody id={FORM_ID} as="form" onSubmit={onSubmit}>
        <Text as="p" color="neutral.500">
          Select the plan(s) you would like to show in the PDF
        </Text>

        {canReorder ? (
          <DndContext onDragEnd={handleDragEnd}>
            <SortableContext items={sortablePlanIds}>
              {fields.map((field, index) => renderPlanField(field, index))}
            </SortableContext>
          </DndContext>
        ) : (
          fields.map((field, index) => renderPlanField(field, index))
        )}
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={props.onClose}>
          Cancel
        </Button>

        <Button type="submit" form={FORM_ID} disabled={selectedPlansCount < PLANS_MIN_COUNT} ml="8">
          Save Changes
        </Button>
      </ModalFooter>
    </Modal>
  );
};
